import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const SESSION_KEY = 'hitster_admin_pin';

interface Reaction {
  id: string;
  eventId: string;
  teamId: string;
  teamName: string;
  naam: string;
  bericht: string;
  status: 'pending' | 'approved';
  ip: string;
  aangemaakt: string;
}

const ReactieBeheer = () => {
  const [pin, setPin] = useState(sessionStorage.getItem(SESSION_KEY) || '');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending');
  const [actionError, setActionError] = useState('');

  const fetchReactions = async (adminPin: string) => {
    setLoading(true);
    setActionError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/reactions`, {
        headers: { 'X-Admin-Pin': adminPin },
      });
      if (res.status === 401) {
        setPinError('Onjuiste PIN');
        setPin('');
        sessionStorage.removeItem(SESSION_KEY);
        setLoading(false);
        return;
      }
      if (res.ok) {
        setReactions(await res.json());
      }
    } catch {
      setActionError('Kan geen verbinding maken');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (pin) fetchReactions(pin);
  }, [pin]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    setPin(pinInput);
    sessionStorage.setItem(SESSION_KEY, pinInput);
  };

  const handleApprove = async (id: string) => {
    setActionError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/reactions/${id}/approve`, {
        method: 'PUT',
        headers: { 'X-Admin-Pin': pin },
      });
      if (res.ok) {
        setReactions(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
      } else {
        setActionError('Goedkeuren mislukt');
      }
    } catch {
      setActionError('Kan geen verbinding maken');
    }
  };

  const handleDelete = async (id: string) => {
    setActionError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/reactions/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Pin': pin },
      });
      if (res.ok || res.status === 204) {
        setReactions(prev => prev.filter(r => r.id !== id));
      } else {
        setActionError('Verwijderen mislukt');
      }
    } catch {
      setActionError('Kan geen verbinding maken');
    }
  };

  if (!pin) {
    return (
      <div className="participant-scores" style={{ maxWidth: 400, margin: '0 auto', padding: '2rem 1rem' }}>
        <h2 style={{ color: '#e2b96e', marginBottom: '1.5rem' }}>🔐 Reactiebeheer</h2>
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          <input
            type="password"
            placeholder="Voer PIN in"
            value={pinInput}
            onChange={e => setPinInput(e.target.value)}
            autoFocus
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8,
              color: '#fff',
              fontSize: '1rem',
              padding: '0.65rem 0.85rem',
            }}
          />
          {pinError && <div className="ps-reaction-error">{pinError}</div>}
          <button
            type="submit"
            className="ps-reaction-submit"
            style={{ alignSelf: 'flex-start' }}
          >
            Inloggen
          </button>
        </form>
      </div>
    );
  }

  const pending = reactions.filter(r => r.status === 'pending');
  const approved = reactions.filter(r => r.status === 'approved');
  const shown = activeTab === 'pending' ? pending : approved;

  return (
    <div className="participant-scores" style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
        <h2 style={{ color: '#e2b96e', margin: 0 }}>💬 Reactiebeheer</h2>
        <button
          onClick={() => { setPin(''); sessionStorage.removeItem(SESSION_KEY); }}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
        >
          Uitloggen
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem' }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            background: activeTab === 'pending' ? 'rgba(226,185,110,0.2)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${activeTab === 'pending' ? '#e2b96e' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 8,
            color: activeTab === 'pending' ? '#e2b96e' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.88rem',
            padding: '0.4rem 1rem',
          }}
        >
          In behandeling ({pending.length})
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          style={{
            background: activeTab === 'approved' ? 'rgba(100,220,130,0.15)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${activeTab === 'approved' ? '#7de8a0' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 8,
            color: activeTab === 'approved' ? '#7de8a0' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.88rem',
            padding: '0.4rem 1rem',
          }}
        >
          Goedgekeurd ({approved.length})
        </button>
        <button
          onClick={() => fetchReactions(pin)}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            marginLeft: 'auto',
            padding: '0.4rem 0.8rem',
          }}
        >
          ↻ Verversen
        </button>
      </div>

      {actionError && <div className="ps-reaction-error" style={{ marginBottom: '1rem' }}>{actionError}</div>}

      {loading && <p style={{ color: 'rgba(255,255,255,0.5)' }}>Laden...</p>}

      {!loading && shown.length === 0 && (
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>Geen reacties</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {shown.map(r => (
          <div key={r.id} className="ps-reaction-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#e2b96e', fontWeight: 600, fontSize: '0.85rem' }}>
                  {r.naam} <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>— {r.teamName}</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', margin: '0.3rem 0' }}>{r.bericht}</div>
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                  {new Date(r.aangemaakt).toLocaleString('nl-NL')} · IP: {r.ip}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                {r.status === 'pending' && (
                  <button
                    onClick={() => handleApprove(r.id)}
                    style={{
                      background: 'rgba(100,220,130,0.15)',
                      border: '1px solid rgba(100,220,130,0.3)',
                      borderRadius: 6,
                      color: '#7de8a0',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      padding: '0.3rem 0.6rem',
                    }}
                  >
                    ✓ Goedkeuren
                  </button>
                )}
                <button
                  onClick={() => handleDelete(r.id)}
                  style={{
                    background: 'rgba(255,80,80,0.1)',
                    border: '1px solid rgba(255,80,80,0.25)',
                    borderRadius: 6,
                    color: '#ff9e9e',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: '0.3rem 0.6rem',
                  }}
                >
                  ✕ Verwijderen
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReactieBeheer;
